import React, { ReactNode } from 'react';
import { Trans } from '@lingui/macro';

import { Close } from '@rover/icons';
import { Button, Flex, Link } from '@rover/kibble/core';
import { Font, MQ, Spacing, ZIndex } from '@rover/kibble/styles';
import { AnnouncementType } from '@rover/types';

import useGetAnnouncementStyle from './hooks/useGetAnnouncementStyle';

export type Props = {
  actionLinkPath: string | null;
  actionText: string;
  announcementType: AnnouncementType;
  backgroundImageSrc: string | null;
  isDismissible?: boolean;
  onClickAction?: () => void;
  onDismiss?: () => void;
  textDescription: string;
  textHeadline: string;
  edgeId?: string;
};

const Announcement = ({
  actionLinkPath,
  actionText,
  announcementType = AnnouncementType.INFO,
  backgroundImageSrc,
  isDismissible = true,
  onClickAction,
  textDescription,
  textHeadline,
  onDismiss,
  edgeId,
}: Props): JSX.Element => {
  const { background, color, border, fill } = useGetAnnouncementStyle(
    announcementType,
    backgroundImageSrc
  );

  // To speed up rolling out Edge Caching to EU City Service pages, we're hardcoding data for
  // the GDPR banner in the City Service pages, which means duplicating the translations
  // on the frontend, which means the description will be a translated string
  // in that case.  TODO: fix this in DEV-60859.
  const temporaryDisplayDescriptionFix = (descriptionOrTranslated: string) => {
    if (!descriptionOrTranslated) {
      return null;
    }
    if (typeof descriptionOrTranslated === 'string') {
      return <>{descriptionOrTranslated}&nbsp;</>;
    }
    return (
      <>
        {descriptionOrTranslated}
        &nbsp;
      </>
    );
  };

  // Same thing with action text - when announcement is hardcoded, action text will be an object
  const temporaryActionTextTranslationFix: (
    actionTextOrTranslated: string
  ) => NonNullable<ReactNode> = (actionTextOrTranslated) => {
    if (typeof actionTextOrTranslated === 'string') {
      return actionTextOrTranslated;
    }
    return <Trans id={actionTextOrTranslated} />;
  };

  return (
    <div edge-id={edgeId}>
      <Flex
        data-testid="announcement"
        justifyContent={['left', 'center']}
        alignItems="center"
        sx={{
          background,
          color,
          border,
          padding: Spacing.M.toString(),
          position: 'relative',
          lineHeight: Spacing.L.toString(),
          zIndex: ZIndex.DROPDOWN.toString(),

          span: {
            color,
          },

          ...(backgroundImageSrc
            ? {
                [MQ.XS_DOWN.toString()]: {
                  backgroundSize: 'auto',
                },
              }
            : {}),
        }}
        width="100%"
      >
        <Flex
          sx={{
            fontSize: Font.FontSize.L.toString(),
            flexWrap: 'wrap',
          }}
        >
          {textHeadline && (
            <>
              <strong>{textHeadline}</strong>{' '}
            </>
          )}
          {temporaryDisplayDescriptionFix(textDescription)}
          {actionText && (actionLinkPath || onClickAction) && (
            <Link
              href={actionLinkPath || ''}
              onClick={onClickAction}
              sx={{
                fontWeight: Font.FontWeight.BOLD.toString(),
                whiteSpace: 'nowrap',
              }}
            >
              {temporaryActionTextTranslationFix(actionText)}
            </Link>
          )}
        </Flex>

        {isDismissible && (
          <Flex ml="6x">
            <Button
              tabIndex={0}
              aria-label="Close Alert"
              variant="flat"
              onClick={onDismiss}
              pt="2x"
              pb="0x"
              sx={{
                '&:hover': {
                  backgroundColor: 'inherit',
                },
              }}
            >
              <Close height="16" width="16" fill={fill} />
            </Button>
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export default Announcement;
